import { useState } from 'react';
import { User, Mail, LogOut, Heart, Download, Calendar } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from '../hooks/useLocalStorage';
import ImageGrid from '../components/ImageGrid';
import ImageModal from '../components/ImageModal';
import SpotlightCard from '../components/SpotlightCard';

// Enhanced Profile page with user stats and favorites
const Profile = ({ addToast }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [favorites] = useLocalStorage('favorites', []);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      addToast('Logged out successfully', 'success');
      navigate('/');
    } catch (error) {
      addToast('Logout failed', 'error');
    }
  };

  // Handle image download
  const handleDownload = async (image) => {
    try {
      addToast('Downloading...', 'info');
      const downloadUrl = `${image.urls.full}?w=1920&q=80&force=true`;
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pixelbazar-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      addToast('Image downloaded successfully!', 'success');
    } catch (error) {
      addToast('Failed to download image', 'error');
    }
  };

  // Get user initials
  const getUserInitials = () => {
    return user?.email ? user.email.charAt(0).toUpperCase() : '?';
  };

  // Get join date
  const getJoinDate = () => {
    if (user?.metadata?.creationTime) {
      return new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    return 'Recently';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-16 sm:py-20 px-4">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            {/* Avatar */}
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-4xl sm:text-5xl font-bold border-2 border-white/30 shadow-2xl">
              {getUserInitials()}
            </div>

            {/* User Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">
                {user?.displayName || 'Welcome'}
              </h1>
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2 sm:gap-4 text-blue-100 mb-4">
                <div className="flex items-center gap-2">
                  <Mail size={18} />
                  <span className="text-sm sm:text-base">{user?.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span className="text-sm sm:text-base">Joined {getJoinDate()}</span>
                </div>
              </div>
              <p className="text-blue-100 text-sm sm:text-base">
                Explore and save your favorite images
              </p>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
            >
              <LogOut size={20} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {/* Favorites Stat */}
          <SpotlightCard className="bg-white dark:bg-gray-950 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Heart size={24} className="text-red-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Favorites</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{favorites.length}</p>
              </div>
            </div>
          </SpotlightCard>

          {/* Downloads Stat */}
          <SpotlightCard className="bg-white dark:bg-gray-950 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Download size={24} className="text-blue-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Downloads</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">0</p>
              </div>
            </div>
          </SpotlightCard>

          {/* Account Status */}
          <SpotlightCard className="bg-white dark:bg-gray-950 rounded-2xl p-6 border border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <User size={24} className="text-green-500" />
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 text-sm">Status</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">Active</p>
              </div>
            </div>
          </SpotlightCard>
        </div>

        {/* Favorites Section */}
        <div>
          <div className="mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              My Favorites
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {favorites.length} image{favorites.length !== 1 ? 's' : ''} saved
            </p>
          </div>

          {favorites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-800">
              <div className="text-6xl mb-4">❤️</div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
                No Favorites Yet
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
                Start exploring and save your favorite images to see them here
              </p>
            </div>
          ) : (
            <ImageGrid
              images={favorites}
              onImageClick={(image) => {
                setSelectedImage(image);
                setIsModalOpen(true);
              }}
              onDownload={handleDownload}
              onLoadMore={() => {}}
              hasMore={false}
              isLoading={false}
            />
          )}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        image={selectedImage}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onDownload={handleDownload}
        addToast={addToast}
      />
    </div>
  );
};

export default Profile;
